/**
 * Definitions Index - Aggregates all definition modules
 *
 * This module provides:
 * 1. Namespaced imports for direct access to schema/definition/type
 * 2. allDefinitions object for builders and UI builders
 * 3. Type re-exports for convenience
 *
 * This is the subpath entry point for `@hopdrive/sdk-configs/definitions`.
 * Import from here when you need definition metadata for UI generators.
 */

// =============================================================================
// Definition System - Re-exported from types for definition consumers
// =============================================================================
export {
  // Definition helper function
  define,
  // Category and role constants for UI grouping and access control
  CATEGORIES,
  ROLES,
  // Kind constants for distinguishing settings from outputs
  DEFINITION_KINDS,
} from '../types.js';

export type {
  // Core definition types
  Definition,
  DefinitionCategory,
  DefinitionKind,
  Role,
} from '../types.js';

// Import each definition module with namespace
import * as version from './version.js';
import * as apiEnabled from './api-enabled.js';
import * as apiConfig from './api-config.js';
import * as priceEstimates from './price-estimates.js';
import * as distanceEstimates from './distance-estimates.js';
import * as allowEmailNotifications from './allow-email-notifications.js';
import * as allowTriangleMoves from './allow-triangle-moves.js';
import * as enableCsvImport from './enable-csv-import.js';
import * as publicWorkflowsets from './public-workflowsets.js';
import * as rideshareCallingEnabled from './rideshare-calling-enabled.js';
import * as rideProviderAlgorithmKey from './ride-provider-algorithm-key.js';
import * as passthroughRideshareSurges from './passthrough-rideshare-surges.js';
import * as requireThirdPartyPayer from './require-third-party-payer.js';
import * as autoAssignRef from './auto-assign-ref.js';
import * as defaultMoveType from './default-move-type.js';
import * as allowedMoveTypes from './allowed-move-types.js';
import * as allowedTemplates from './allowed-templates.js';
import * as addMovesAlways from './add-moves-always.js';
import * as addMovesDisabled from './add-moves-disabled.js';
import * as cancellationFee from './cancellation-fee.js';
import * as delayPricePerMinute from './delay-price-per-minute.js';
import * as dryRunBehavior from './dry-run-behavior.js';
import * as fuelMultiplier from './fuel-multiplier.js';
import * as platformChargeAmount from './platform-charge-amount.js';
import * as defaultWorkflowSetId from './default-workflow-set-id.js';
import * as pickupWorkflowId from './pickup-workflow-id.js';
import * as deliveryWorkflowId from './delivery-workflow-id.js';
import * as defaultPickupWorkflowId from './default-pickup-workflow-id.js';
import * as defaultDeliveryWorkflowId from './default-delivery-workflow-id.js';
import * as defaultPayer from './default-payer.js';
import * as referenceNumLabel from './reference-num-label.js';
import * as referenceNumRequiredForPickup from './reference-num-required-for-pickup.js';
import * as referenceNumRequiredForReturn from './reference-num-required-for-return.js';
import * as referenceNumReportEnabled from './reference-num-report-enabled.js';
import * as referenceNumReportLabel from './reference-num-report-label.js';
import * as serviceableRadiusMiles from './serviceable-radius-miles.js';
import * as customerServiceableRadiusMiles from './customer-serviceable-radius-miles.js';
import * as serviceableRadiusByDelivery from './serviceable-radius-by-delivery.js';
import * as serviceableRadiusByPickup from './serviceable-radius-by-pickup.js';
import * as rideTypeOverride from './ride-type-override.js';
import * as branding from './branding.js';
import * as appraisals from './appraisals.js';
import * as appointments from './appointments.js';
import * as appointmentManagement from './appointment-management.js';
import * as hopdriveContacts from './hopdrive-contacts.js';
import * as customerValues from './customer-values.js';
import * as fuel from './fuel.js';
import * as fraudAudit from './fraud-audit.js';
import * as dms from './dms.js';
import * as emailScrapeConfig from './email-scrape-config.js';
import * as rcConfig from './rc_config.js';
import * as privateDefinition from './private.js';
import * as routing from './routing.js';
import * as connectorTriggers from './connector-triggers.js';
import * as minimumCertificationIds from './minimum-certification-ids.js';
import * as billingFrequency from './billing-frequency.js';
import * as paymentTerms from './payment-terms.js';
import * as autoPay from './auto-pay.js';

// Driver pipeline definitions
import * as driverPipeline from './driver-pipeline.js';

// Operational state definitions (system-managed outputs)
import * as isShuttle from './isShuttle.js';
import * as firstShuttleMove from './firstShuttleMove.js';
import * as lastShuttleMove from './lastShuttleMove.js';
import * as chaseId from './chase-id.js';
import * as parentChaseId from './parent-chase-id.js';
import * as batch from './batch.js';
import * as rideOrchestration from './ride-orchestration.js';
import * as superDispatch from './superDispatch.js';
import * as referenceNumbers from './referenceNumbers.js';
import * as isGhost from './isGhost.js';
import * as vin from './vin.js';
import * as triangleMove from './triangle-move.js';
import * as isFlex from './isFlex.js';
import * as moveInput from './moveInput.js';

// Export namespaced modules for direct access
export {
  version,
  apiEnabled,
  apiConfig,
  priceEstimates,
  distanceEstimates,
  allowEmailNotifications,
  allowTriangleMoves,
  enableCsvImport,
  publicWorkflowsets,
  rideshareCallingEnabled,
  rideProviderAlgorithmKey,
  passthroughRideshareSurges,
  requireThirdPartyPayer,
  autoAssignRef,
  defaultMoveType,
  allowedMoveTypes,
  allowedTemplates,
  addMovesAlways,
  addMovesDisabled,
  cancellationFee,
  delayPricePerMinute,
  dryRunBehavior,
  fuelMultiplier,
  platformChargeAmount,
  defaultWorkflowSetId,
  pickupWorkflowId,
  deliveryWorkflowId,
  defaultPickupWorkflowId,
  defaultDeliveryWorkflowId,
  defaultPayer,
  referenceNumLabel,
  referenceNumRequiredForPickup,
  referenceNumRequiredForReturn,
  referenceNumReportEnabled,
  referenceNumReportLabel,
  serviceableRadiusMiles,
  customerServiceableRadiusMiles,
  serviceableRadiusByDelivery,
  serviceableRadiusByPickup,
  rideTypeOverride,
  branding,
  appraisals,
  appointments,
  appointmentManagement,
  hopdriveContacts,
  customerValues,
  fuel,
  fraudAudit,
  dms,
  emailScrapeConfig,
  rcConfig,
  privateDefinition,
  routing,
  // Driver pipeline definitions
  driverPipeline,
  connectorTriggers,
  // Non-JSONB storage definitions
  minimumCertificationIds,
  billingFrequency,
  paymentTerms,
  autoPay,
  // Operational state definitions
  isShuttle,
  firstShuttleMove,
  lastShuttleMove,
  chaseId,
  parentChaseId,
  batch,
  rideOrchestration,
  superDispatch,
  referenceNumbers,
  isGhost,
  vin,
  triangleMove,
  isFlex,
  moveInput,
};

/**
 * Aggregate all definitions for builders and UI builders.
 * Keyed by the config property name (e.g., 'api_enabled', 'branding').
 */
export const allDefinitions = {
  [version.definition.key]: version.definition,
  [apiEnabled.definition.key]: apiEnabled.definition,
  [apiConfig.definition.key]: apiConfig.definition,
  [priceEstimates.definition.key]: priceEstimates.definition,
  [distanceEstimates.definition.key]: distanceEstimates.definition,
  [allowEmailNotifications.definition.key]: allowEmailNotifications.definition,
  [allowTriangleMoves.definition.key]: allowTriangleMoves.definition,
  [enableCsvImport.definition.key]: enableCsvImport.definition,
  [publicWorkflowsets.definition.key]: publicWorkflowsets.definition,
  [rideshareCallingEnabled.definition.key]: rideshareCallingEnabled.definition,
  [rideProviderAlgorithmKey.definition.key]: rideProviderAlgorithmKey.definition,
  [passthroughRideshareSurges.definition.key]: passthroughRideshareSurges.definition,
  [requireThirdPartyPayer.definition.key]: requireThirdPartyPayer.definition,
  [autoAssignRef.definition.key]: autoAssignRef.definition,
  [defaultMoveType.definition.key]: defaultMoveType.definition,
  [allowedMoveTypes.definition.key]: allowedMoveTypes.definition,
  [allowedTemplates.definition.key]: allowedTemplates.definition,
  [addMovesAlways.definition.key]: addMovesAlways.definition,
  [addMovesDisabled.definition.key]: addMovesDisabled.definition,
  [cancellationFee.definition.key]: cancellationFee.definition,
  [delayPricePerMinute.definition.key]: delayPricePerMinute.definition,
  [dryRunBehavior.definition.key]: dryRunBehavior.definition,
  [fuelMultiplier.definition.key]: fuelMultiplier.definition,
  [platformChargeAmount.definition.key]: platformChargeAmount.definition,
  [defaultWorkflowSetId.definition.key]: defaultWorkflowSetId.definition,
  [pickupWorkflowId.definition.key]: pickupWorkflowId.definition,
  [deliveryWorkflowId.definition.key]: deliveryWorkflowId.definition,
  [defaultPickupWorkflowId.definition.key]: defaultPickupWorkflowId.definition,
  [defaultDeliveryWorkflowId.definition.key]: defaultDeliveryWorkflowId.definition,
  [defaultPayer.definition.key]: defaultPayer.definition,
  [referenceNumLabel.definition.key]: referenceNumLabel.definition,
  [referenceNumRequiredForPickup.definition.key]: referenceNumRequiredForPickup.definition,
  [referenceNumRequiredForReturn.definition.key]: referenceNumRequiredForReturn.definition,
  [referenceNumReportEnabled.definition.key]: referenceNumReportEnabled.definition,
  [referenceNumReportLabel.definition.key]: referenceNumReportLabel.definition,
  [serviceableRadiusMiles.definition.key]: serviceableRadiusMiles.definition,
  [customerServiceableRadiusMiles.definition.key]: customerServiceableRadiusMiles.definition,
  [serviceableRadiusByDelivery.definition.key]: serviceableRadiusByDelivery.definition,
  [serviceableRadiusByPickup.definition.key]: serviceableRadiusByPickup.definition,
  [rideTypeOverride.definition.key]: rideTypeOverride.definition,
  [branding.definition.key]: branding.definition,
  [appraisals.definition.key]: appraisals.definition,
  [appointments.definition.key]: appointments.definition,
  [appointmentManagement.definition.key]: appointmentManagement.definition,
  [hopdriveContacts.definition.key]: hopdriveContacts.definition,
  [customerValues.definition.key]: customerValues.definition,
  [fuel.definition.key]: fuel.definition,
  [fraudAudit.definition.key]: fraudAudit.definition,
  [dms.definition.key]: dms.definition,
  [emailScrapeConfig.definition.key]: emailScrapeConfig.definition,
  [rcConfig.definition.key]: rcConfig.definition,
  [privateDefinition.definition.key]: privateDefinition.definition,
  [routing.definition.key]: routing.definition,
  // Driver pipeline definitions
  [driverPipeline.definition.key]: driverPipeline.definition,
  [connectorTriggers.definition.key]: connectorTriggers.definition,
  // Non-JSONB storage definitions
  [minimumCertificationIds.definition.key]: minimumCertificationIds.definition,
  [billingFrequency.definition.key]: billingFrequency.definition,
  [paymentTerms.definition.key]: paymentTerms.definition,
  [autoPay.definition.key]: autoPay.definition,
  // Operational state definitions
  [isShuttle.definition.key]: isShuttle.definition,
  [firstShuttleMove.definition.key]: firstShuttleMove.definition,
  [lastShuttleMove.definition.key]: lastShuttleMove.definition,
  [chaseId.definition.key]: chaseId.definition,
  [parentChaseId.definition.key]: parentChaseId.definition,
  [batch.definition.key]: batch.definition,
  [rideOrchestration.definition.key]: rideOrchestration.definition,
  [superDispatch.definition.key]: superDispatch.definition,
  [referenceNumbers.definition.key]: referenceNumbers.definition,
  [isGhost.definition.key]: isGhost.definition,
  [vin.definition.key]: vin.definition,
  [triangleMove.definition.key]: triangleMove.definition,
  [isFlex.definition.key]: isFlex.definition,
  [moveInput.definition.key]: moveInput.definition,
} as const;

// Re-export types for convenience
export type { Version } from './version.js';
export type { ApiEnabled } from './api-enabled.js';
export type { ApiConfig } from './api-config.js';
export type { PriceEstimates } from './price-estimates.js';
export type { DistanceEstimates } from './distance-estimates.js';
export type { AllowEmailNotifications } from './allow-email-notifications.js';
export type { AllowTriangleMoves } from './allow-triangle-moves.js';
export type { EnableCsvImport } from './enable-csv-import.js';
export type { PublicWorkflowsets } from './public-workflowsets.js';
export type { RideshareCallingEnabled } from './rideshare-calling-enabled.js';
export type { RideProviderAlgorithmKey } from './ride-provider-algorithm-key.js';
export type { PassthroughRideshareSurges } from './passthrough-rideshare-surges.js';
export type { RequireThirdPartyPayer } from './require-third-party-payer.js';
export type { AutoAssignRef } from './auto-assign-ref.js';
export type { MoveType, DefaultMoveType } from './default-move-type.js';
export type { AllowedMoveTypes } from './allowed-move-types.js';
export type { AllowedTemplates } from './allowed-templates.js';
export type { AddMovesAlways } from './add-moves-always.js';
export type { AddMovesDisabled } from './add-moves-disabled.js';
export type { CancellationFee } from './cancellation-fee.js';
export type { DelayPricePerMinute } from './delay-price-per-minute.js';
export type { DryRunBehavior } from './dry-run-behavior.js';
export type { FuelMultiplier } from './fuel-multiplier.js';
export type { PlatformChargeAmount } from './platform-charge-amount.js';
export type { DefaultWorkflowSetId } from './default-workflow-set-id.js';
export type { PickupWorkflowId } from './pickup-workflow-id.js';
export type { DeliveryWorkflowId } from './delivery-workflow-id.js';
export type { DefaultPickupWorkflowId } from './default-pickup-workflow-id.js';
export type { DefaultDeliveryWorkflowId } from './default-delivery-workflow-id.js';
export type { DefaultPayer } from './default-payer.js';
export type { ReferenceNumLabel } from './reference-num-label.js';
export type { ReferenceNumRequiredForPickup } from './reference-num-required-for-pickup.js';
export type { ReferenceNumRequiredForReturn } from './reference-num-required-for-return.js';
export type { ReferenceNumReportEnabled } from './reference-num-report-enabled.js';
export type { ReferenceNumReportLabel } from './reference-num-report-label.js';
export type { ServiceableRadiusMiles } from './serviceable-radius-miles.js';
export type { CustomerServiceableRadiusMiles } from './customer-serviceable-radius-miles.js';
export type { ServiceableRadiusByDelivery } from './serviceable-radius-by-delivery.js';
export type { ServiceableRadiusByPickup } from './serviceable-radius-by-pickup.js';
export type { RideTypeOverride } from './ride-type-override.js';
export type { Branding } from './branding.js';
export type { Appraisals, AppraisalTimestamp } from './appraisals.js';
export type { Appointments } from './appointments.js';
export type { AppointmentManagement } from './appointment-management.js';
export type { HopdriveContacts } from './hopdrive-contacts.js';
export type { CustomerValues } from './customer-values.js';
export type { Fuel } from './fuel.js';
export type { FraudAudit } from './fraud-audit.js';
export type { Dms } from './dms.js';
export type { EmailScrapeConfig, EmailScrapeMapping, EmailScrapePipelineStep } from './email-scrape-config.js';
export type { RcConfig, RcEmailScrapeConfig } from './rc_config.js';
export type { Private } from './private.js';
export type { Routing } from './routing.js';
export type {
  IntegrationConfig,
  AuthProviderConfig,
  OAuth2Config,
  ApiKeyAuthConfig,
  BasicAuthConfig,
  AutocompleteConfig,
  FetchConfig,
  EnrichmentConfig,
  FieldMapping,
  TransformConfig,
  // Canonical types - the 4 types form code needs to know
  AutocompleteRequest,
  AutocompleteResult,
  FetchRequest,
  FetchResult,
} from './connector-triggers.js';

// Driver pipeline types
export type { DriverPipeline } from './driver-pipeline.js';

// Non-JSONB storage types
export type { MinimumCertificationKeys } from './minimum-certification-ids.js';
export type { BillingFrequency } from './billing-frequency.js';
export type { PaymentTerms } from './payment-terms.js';
export type { AutoPay } from './auto-pay.js';

// Operational state types
export type { IsShuttle } from './isShuttle.js';
export type { FirstShuttleMove } from './firstShuttleMove.js';
export type { LastShuttleMove } from './lastShuttleMove.js';
export type { ChaseId } from './chase-id.js';
export type { ParentChaseId } from './parent-chase-id.js';
export type { Batch } from './batch.js';
export type { RideOrchestration, RideshareProvider } from './ride-orchestration.js';
export type { SuperDispatch } from './superDispatch.js';
export type { ReferenceNumbers } from './referenceNumbers.js';
export type { IsGhost } from './isGhost.js';
export type { Vin } from './vin.js';
export type { TriangleMove } from './triangle-move.js';
export type { IsFlex } from './isFlex.js';
export type { MoveInput } from './moveInput.js';

// Re-export schemas that may be needed by consumers
export { MoveTypeSchema } from './default-move-type.js';
export { RideTypeOverrideSchema } from './ride-type-override.js';
export { AppraisalTimestampSchema } from './appraisals.js';
export { EmailScrapePipelineStepSchema, EmailScrapeMappingSchema } from './email-scrape-config.js';
export { RcEmailScrapeConfigSchema } from './rc_config.js';
export { RideshareProviderSchema } from './ride-orchestration.js';
export {
  IntegrationSchema,
  TransformConfigSchema,
  FieldMappingSchema,
  AuthProviderSchema,
  AutocompleteTriggerSchema,
  FetchTriggerSchema,
  FetchResultMappingsSchema,
  EnrichmentSchema,
  // Canonical field constants
  AUTOCOMPLETE_FIELDS,
  FETCH_RESULT_FIELDS,
} from './connector-triggers.js';
